import { connect } from '@cerebral/react'
import { Box, Divider, Grid, Typography } from '@mui/material'
import { Delete } from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import { sequences, state } from 'cerebral'
import { Field } from 'formik'
import { CheckboxWithLabel, TextField } from 'formik-mui'
import { pick } from 'ramda'
import { isNumber } from 'ramda-adjunct'
import React, { useCallback } from 'react'
import schemaSettings from '../../../../../shared/schemas/resort-schema'
import brand from '../../../../lib/util/brand'
import { omitStateFlag } from '../../../../lib/util/cerebral'
import ValidatedForm from '../../../blocks/ValidatedForm'
import DropUpload from '../../../controls/DropUpload'
import LimitedTextField from '../../../controls/LimitedTextField'
import Notice from '../../../elements/Notice'
import ThemedIconButton from '../../../controls/ThemedIconButton'
import { AddButton } from '../../../elements/AddButton'
import { resortUpdateFields } from '../../../../modules/account/actions'

const { settingsFormSchema, nameTemplateFields } = schemaSettings

const isQuiltt = brand.name === 'Quiltt'

const useStyles = makeStyles((theme) => ({
  DropContainer: {
    borderRadius: theme.spacing(1),
  },
}))

const GeneralSettings = connect(
  {
    organization: state`account.organization`,
    saveSettings: sequences`account.saveSettings`,
    theme: state`account.organization.theme`,
    serviceRequestsEnabled: state`account.packages.service-requests`,
  },
  (props) => ({
    ...props,
    saveSettings: (values) => props.saveSettings({ values: pick(resortUpdateFields, values) }),
  }),
  ({ organization, saveSettings, theme: alpineTheme = {}, serviceRequestsEnabled }) => {
    const classes = useStyles()

    return organization?._id ? (
      <ValidatedForm title="General Settings" section="Organization" initialValues={organization} schema={settingsFormSchema} onSubmit={saveSettings}>
        {({ setFieldValue, errors, values: { logo, introLogo, birthdayUpdates: { automaticallyCreate } = {}, serviceRequestTypes = [] }, isSubmitting }) => {
          const addServiceRequest = useCallback(() => {
            setFieldValue('serviceRequestTypes', serviceRequestTypes.concat(''))
          }, [setFieldValue, serviceRequestTypes])

          return (
            <Box flexDirection="row">
              {(!isNumber(organization.latitude) || !isNumber(organization.longitude) || !organization.timezone) && (
                <Notice variant="info">Please enter an address for {organization.name} to finish setting up your account.</Notice>
              )}
              <Grid spacing={4} container>
                <Grid md={6} xs={12} item>
                  <Typography variant="h4">Address</Typography>
                  <Field label="Street" name="address.street1" component={TextField} />
                  <Field name="address.street2" component={TextField} />
                  <Field label="City" name="address.city" component={TextField} />
                  <Field label="State" name="address.state" component={TextField} />
                  <Field label="Zip" name="address.zip" component={TextField} />
                </Grid>

                <Grid md={6} xs={12} item>
                  <Typography variant="h4" gutterBottom>
                    Logo on brand color
                  </Typography>
                  <Box padding={2} className={classes.DropContainer} style={{ backgroundColor: alpineTheme.AlpineBGDark }}>
                    <DropUpload optional ratio={9 / 16} image={omitStateFlag(logo)} onChange={(image) => setFieldValue('logo', image)} error={errors.logo} />
                  </Box>
                  <Box mt={3} mb={3}>
                    <Divider />
                  </Box>
                  <Typography variant="h4" gutterBottom>
                    Logo on light
                  </Typography>
                  <DropUpload
                    optional
                    ratio={9 / 16}
                    image={omitStateFlag(introLogo)}
                    onChange={(image) => setFieldValue('introLogo', image)}
                    error={errors.introLogo}
                  />
                </Grid>
              </Grid>

              {isQuiltt && (
                <>
                  <Box mt={3} mb={3}>
                    <Divider />
                  </Box>
                  <Box mb={2}>
                    <Typography variant="h4">Resident names for publication</Typography>
                  </Box>
                  <Box mb={2}>
                    <Notice>The following placeholders will be replaced with resident information: {nameTemplateFields}</Notice>
                    <Field label="Name publication template" name="publishedNameTemplate" schema={settingsFormSchema} component={LimitedTextField} />
                  </Box>
                  <Box mb={2}>
                    <Typography variant="h4">Notifications</Typography>
                  </Box>
                  <Field
                    Label={{ label: 'Automatically create birthday announcements?' }}
                    name="birthdayUpdates.automaticallyCreate"
                    type="checkbox"
                    component={CheckboxWithLabel}
                  />
                  {automaticallyCreate && (
                    <>
                      <Notice>
                        On a resident&apos;s birthday, a notification for signage will be automatically created. The following placeholders will be replaced
                        with resident information: {nameTemplateFields}
                      </Notice>
                      <Field
                        required
                        label="Primary Update Template"
                        name="birthdayUpdates.primaryTemplate"
                        schema={settingsFormSchema}
                        component={LimitedTextField}
                      />
                      <Field
                        multiline
                        minRows="4"
                        label="Details Template (optional)"
                        name="birthdayUpdates.detailsTemplate"
                        schema={settingsFormSchema}
                        component={LimitedTextField}
                      />
                    </>
                  )}

                  {serviceRequestsEnabled && (
                    <Box my={6}>
                      <Typography variant="h4">Service Requests</Typography>
                      {Boolean(serviceRequestTypes?.length) &&
                        serviceRequestTypes.map((serviceRequest, index) => {
                          return (
                            <Box mt={1} key={index}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={9} md={6}>
                                  <Field label="Service Request" name={`serviceRequestTypes.${index}`} component={TextField} />
                                </Grid>

                                <Grid item>
                                  <Box p={1}>
                                    <ThemedIconButton
                                      color="danger"
                                      onClick={() => {
                                        setFieldValue(
                                          'serviceRequestTypes',
                                          serviceRequestTypes.filter((s) => s !== serviceRequest)
                                        )
                                      }}
                                    >
                                      <Delete />
                                    </ThemedIconButton>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          )
                        })}
                      <Box py={1}>
                        <Divider />
                      </Box>
                      <Box display="flex" justifyContent="flex-start">
                        <AddButton disabled={isSubmitting} onClick={addServiceRequest} title="Add service request" />
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )
        }}
      </ValidatedForm>
    ) : null
  }
)

export default GeneralSettings
